import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/auth';
import { config } from '../config/env';
import { Sparkles, Zap, Shield, Eye, EyeOff, Smartphone, Lock } from 'lucide-react';
import './Auth.css';

function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('signin');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    // Pre-fill email from invitation flow
    useEffect(() => {
        if (location.state?.email) {
            setEmail(location.state.email);
        }
        if (location.state?.message) {
            setSuccessMessage(location.state.message);
        }
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(email, password);
            // Only navigate if login was successful
            if (result && result.user) {
                // Check if there's a returnTo path from invitation flow
                const returnTo = location.state?.returnTo || '/';
                navigate(returnTo);
            }
        } catch (err) {
            // Set error and keep user on login page
            const errorMessage = err.response?.data?.error || err.message || 'Login failed. Please check your credentials.';
            setError(errorMessage);
            console.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSSOLogin = () => {
        // Redirect to backend OAuth proxy
        authService.loginWithSSO();
    };

    return (
        <div className="auth-page">
            {/* Left Side - Login Form */}
            <div className="auth-left">
                <div className="auth-logo">
                    <img
                        src={`${process.env.PUBLIC_URL}/rova-ai-logo-wordmark-white.png`}
                        alt="ROVA AI"
                        className="logo-wordmark"
                        style={{ height: '32px', width: 'auto' }}
                    />
                </div>

                <div className="auth-content">
                    <h1 className="auth-title">Welcome </h1>
                    <p className="auth-subtitle">
                        Log in to access your autonomous testing dashboard.
                    </p>

                    {config.AUTH_MODE === 'external' ? (
                        <div className="auth-external-login">
                            {/* <p className="auth-description" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
                                Please log in with your Scandium account to continue.
                            </p> */}

                            {successMessage && <div className="auth-success">{successMessage}</div>}
                            {error && <div className="auth-error">{error}</div>}

                            <div className="social-buttons">
                                <button
                                    type="button"
                                    className="btn btn-primary social-btn btn-full"
                                    onClick={handleSSOLogin}
                                    style={{ justifyContent: 'center' }}
                                >
                                    <Shield size={18} />
                                    Log in or create an account
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Tabs */}
                            <div className="auth-tabs">
                                <button
                                    className={`auth-tab ${activeTab === 'signin' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('signin')}
                                >
                                    Sign In
                                </button>
                                <Link to="/signup" className={`btn auth-tab ${activeTab === 'signup' ? 'active' : ''}`}>
                                    Create Account
                                </Link>

                            </div>

                            {successMessage && <div className="auth-success">{successMessage}</div>}
                            {error && <div className="auth-error">{error}</div>}

                            <form onSubmit={handleSubmit} className="auth-form">
                                <div className="form-group">
                                    <label className="label">Email Address</label>
                                    <input
                                        type="email"
                                        className="input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="name@company.com"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <div className="label-row">
                                        <label className="label">Password</label>
                                        <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>
                                    </div>
                                    <div className="password-input">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="input"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </button>
                            </form>

                            {/* <div className="divider-text">Or continue with</div>

                            <div className="social-buttons">
                                <button
                                    type="button"
                                    className="btn btn-secondary social-btn btn-full"
                                    onClick={handleSSOLogin}
                                >
                                    <Shield size={18} />
                                    Single Sign-On (SSO)
                                </button>
                            </div> */}
                        </>
                    )}

                </div>
            </div>

            {/* Right Side - Value Props */}
            <div className="auth-right">
                <div className="value-props">
                    <div className="hero-icon">
                        <Sparkles size={32} />
                    </div>
                    <h2 className="hero-title">
                        Accelerate your mobile testing with AI
                    </h2>

                    <div className="feature-list">
                        <div className="feature-item">
                            <div className="feature-icon">
                                <Zap size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">10x Faster Execution</h3>
                                <p className="feature-desc">
                                    Our autonomous agents run parallel tests across thousands of device configurations instantly.
                                </p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Sparkles size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">AI-Powered Testing</h3>
                                <p className="feature-desc">
                                    Intelligent and goal driven automation that adapts to your app's unique flows.
                                </p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Smartphone size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">Cross Platform</h3>
                                <p className="feature-desc">
                                    Seamlessly test across Android and iOS devices with a single unified platform.
                                </p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Shield size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">Security</h3>
                                <p className="feature-desc">
                                    Enterprise-grade security with SOC2 compliance and end-to-end encryption.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="social-proof">
                        <div className="avatars">
                            <div className="avatar">👤</div>
                            <div className="avatar">👤</div>
                            <div className="avatar">👤</div>
                        </div>
                        <span>Loved by productive teams</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
